import {
	getFinalSelectionCount,
	getTableAllSelected,
	getTableSelectedIds,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { Table } from '@tanstack/react-table';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { MasterDocumentsFilterDto } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/types.ts';

export interface ContractBulkActionParams {
	selectedIds?: string[];
	excludedIds?: string[];
	filter?: MasterDocumentsFilterDto;
	search?: string;
	archived?: boolean;
	count?: number;
}

/**
 * Build the selection portion of a bulk-action payload.
 *
 * In greedy (select-all-across-pages) mode the table stores the rows the user
 * *unchecked* as the selection, so those ids are the exclusions. We send them as
 * `excludedIds` together with the current `filter`/`search` so the backend operates
 * on "everything matching the view except these". Otherwise the ids are the explicit
 * selection and go out as `selectedIds`.
 */
export const buildBulkSelectionPayload = ({
	isGreedy,
	ids,
	filter,
	search,
}: {
	isGreedy: boolean;
	ids: string[];
	filter?: MasterDocumentsFilterDto;
	search?: string;
}): Pick<ContractBulkActionParams, 'selectedIds' | 'excludedIds' | 'filter' | 'search'> =>
	isGreedy ? { excludedIds: ids, filter, search } : { selectedIds: ids };

/**
 * Resolve the selection payload from the *live* table state.
 *
 * Reads greedy mode and the selected/excluded ids straight off the table at the
 * moment it is called, so it must be invoked at execution time (inside an action
 * handler) — never cached at render. The bulk-actions factory is not re-created
 * when the table's internal selection flips to greedy, so a render-time snapshot
 * of greedy state goes stale and the wrong branch (selectedIds) gets sent.
 */
export const resolveBulkSelectionPayload = <T extends { id: string }>(
	table: Table<T>,
	filter?: MasterDocumentsFilterDto,
	search?: string
): Pick<ContractBulkActionParams, 'selectedIds' | 'excludedIds' | 'filter' | 'search'> => {
	const { manualPagination, greedySelecting } = getTableMeta(table);
	return buildBulkSelectionPayload({
		isGreedy: manualPagination && greedySelecting,
		ids: getTableSelectedIds(table),
		filter,
		search,
	});
};

export const createBulkActions = <T extends { id: string }>(
	table: Table<T>,
	{
		showArchivedFiles,
		isFetching,
		hasEditorPermission,
		filter,
		search,
		tMipManage,
		toggleArchivedFiles,
		onBulkArchive,
		onBulkDelete,
	}: {
		showArchivedFiles: boolean;
		isFetching: boolean;
		hasEditorPermission: boolean;
		filter?: MasterDocumentsFilterDto;
		search?: string;
		tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
		toggleArchivedFiles: () => void;
		onBulkArchive: (params: ContractBulkActionParams) => void;
		onBulkDelete: (params: ContractBulkActionParams) => void;
	}
): BulkActionGroup<T> => {
	const primary: BulkAction<T>[] = [];
	const secondary: BulkAction<T>[] = [];
	const special: BulkAction<T>[] = [];

	const isAllSelected = getTableAllSelected(table);

	// Resolve lazily, at execution time — see resolveBulkSelectionPayload. Caching
	// the greedy state at render time is what caused the flipped-selection bug.
	const getSelectionPayload = () => resolveBulkSelectionPayload(table, filter, search);

	if (hasEditorPermission) {
		primary.push({
			key: showArchivedFiles ? 'bulk_unarchive' : 'bulk_archive',
			label: showArchivedFiles
				? tMipManage('master_documents.bulk_actions.unarchive')
				: tMipManage('master_documents.bulk_actions.archive'),
			buttonLabel: () => {
				const count = getFinalSelectionCount(table);
				return showArchivedFiles
					? `${tMipManage('master_documents.bulk_actions.unarchive')} (${count})`
					: `${tMipManage('master_documents.bulk_actions.archive')} (${count})`;
			},
			disabled: isFetching,
			onExecute: () =>
				onBulkArchive({
					...getSelectionPayload(),
					archived: !showArchivedFiles,
				}),
		});

		secondary.push({
			key: 'bulk_delete',
			label: isAllSelected
				? tMipManage('master_documents.bulk_actions.delete_filtered')
				: tMipManage('master_documents.bulk_actions.delete_selection'),
			buttonLabel: () => {
				const count = getFinalSelectionCount(table);
				return `${tMipManage('master_documents.bulk_actions.delete')} (${count})`;
			},
			color: 'red',
			disabled: isFetching,
			onExecute: () =>
				onBulkDelete({
					...getSelectionPayload(),
					count: getFinalSelectionCount(table),
				}),
		});
	}

	special.push({
		key: showArchivedFiles ? 'view_active' : 'view_archived',
		label: showArchivedFiles
			? tMipManage('master_documents.bulk_actions.view_active')
			: tMipManage('master_documents.bulk_actions.view_archived'),
		disabled: isFetching,
		executeImmediately: true,
		onExecute: toggleArchivedFiles,
	});

	return { primary, secondary, special };
};
